// Self-sufficient export helpers: use axiosInstance to resolve references
// and build a v3 export bundle with devices + refs (clients, deviceConfigs, events).

import axios from '../../../config/axiosInstance';

/* ------------------------------ Normalizers ------------------------------ */

const normalizeClient = (c) => ({
    _id: c._id,

    name: c.name,
    username: c.username ?? null,
    // password arrives as plain text from /clients/:id per your backend -> keep as-is
    password: c.password ?? null,

    regasus_ClientID: c.regasus_ClientID ?? 'REMOSCAN',
    hosts: Array.isArray(c.hosts) ? c.hosts : [],
    portalLogo: c.portalLogo ?? null,
    portalName: c.portalName ?? null,
    clientColor: c.clientColor ?? null,

    // 6-digit PIN as string (or null)
    devicePin: typeof c.devicePin === 'string' ? c.devicePin : null,

    isDeleted: !!c.isDeleted,
    regasusDownloadUrl: c.regasusDownloadUrl ?? null,
    features: Array.isArray(c.features) ? c.features : [],

    // timestamps
    createdAt: c.createdAt ? new Date(c.createdAt).toISOString() : null,
    updatedAt: c.updatedAt ? new Date(c.updatedAt).toISOString() : null,
});

// Covers 100% of Event model fields and preserves types
const normalizeEvent = (ev) => ({
    _id: ev._id,

    // core fields
    eventId: ev.eventId ?? null,
    name: ev.name ?? null,
    label: ev.label ?? null,
    mnemonic: ev.mnemonic ?? null,

    // location/meta
    location: ev.location ?? null,
    city: ev.city ?? null,
    countryPK: ev.countryPK ?? null,
    host_url: ev.host_url ?? null,

    // dates (ISO strings for portability)
    beginDate: ev.beginDate ? new Date(ev.beginDate).toISOString() : null,
    endDate: ev.endDate ? new Date(ev.endDate).toISOString() : null,

    // client reference – relink-friendly if populated, else raw id
    clientID: ev.clientID && typeof ev.clientID === 'object'
        ? {
            _id: ev.clientID._id,
            name: ev.clientID.name ?? null,
            username: ev.clientID.username ?? null,
        }
        : (ev.clientID ?? null),

    // state
    isActive: ev.isActive !== undefined ? !!ev.isActive : true,
    isDeleted: !!ev.isDeleted,

    // timestamps
    createdAt: ev.createdAt ? new Date(ev.createdAt).toISOString() : null,
    updatedAt: ev.updatedAt ? new Date(ev.updatedAt).toISOString() : null,
});

// Full, rich deviceConfig export (matches your schema closely)
export const toExportDeviceConfig = (dc) => ({
    _id: dc._id,

    // core
    DeviceConfigName: dc.DeviceConfigName ?? null,
    Type: dc.Type ?? null,
    Theme: dc.Theme ?? 'light',

    // relations (object form for robust re-linking)
    SelectedEventId: dc.SelectedEventId
        ? {
            _id: dc.SelectedEventId._id ?? dc.SelectedEventId,
            mnemonic: dc.SelectedEventId.mnemonic ?? null,
            beginDate: dc.SelectedEventId.beginDate ?? null,
        }
        : null,
    clientID: dc.clientID
        ? {
            _id: dc.clientID._id ?? dc.clientID,
            name: dc.clientID.name ?? null,
            username: dc.clientID.username ?? null,
        }
        : null,
    SlackChannel: dc.SlackChannel ?? null,

    // selection
    SelectedProgrammePointId: dc.SelectedProgrammePointId ?? null,
    SelectedProgrammePointName: dc.SelectedProgrammePointName ?? null,
    SelectedLocationId: dc.SelectedLocationId ?? null,

    // visibility/state
    IsVisible: dc.IsVisible !== undefined ? !!dc.IsVisible : true,
    isDeleted: !!dc.isDeleted,

    // self-checkin visuals and printer
    BannerUrl: dc.BannerUrl ?? null,
    ImageLeftUrl: dc.ImageLeftUrl ?? null,
    ImageRightUrl: dc.ImageRightUrl ?? null,
    PrinterUrl: dc.PrinterUrl ?? null,

    // self-checkin toggles
    IsPrintBadgeAnywayEnabled: !!dc.IsPrintBadgeAnywayEnabled,
    IsPrintingEnabled: dc.IsPrintingEnabled !== undefined ? !!dc.IsPrintingEnabled : true,
    IsCreateLeadEnabled: !!dc.IsCreateLeadEnabled,
    participantTypesDisabled: Array.isArray(dc.participantTypesDisabled) ? dc.participantTypesDisabled : [],
    IsEFNCheckEnabled: !!dc.IsEFNCheckEnabled,

    // self-checkin text defaults
    TextWaiting: dc.TextWaiting ?? 'Please provide a QR code',
    TextCheckin: dc.TextCheckin ?? 'Checking QR code',
    TextPrinting: dc.TextPrinting ?? 'Printing badge',
    TextCreating: dc.TextCreating ?? 'Lead created',
    TextDenying: dc.TextDenying ?? 'Cannot proceed',
    TextBlacklist: dc.TextBlacklist ?? 'Your participant type does not allow printing here',
    TextEFNError: dc.TextEFNError ?? 'Bitte registrieren Sie Ihre EFN an einem unserer Counter.',

    // entrance control
    Mode: dc.Mode ?? 'entranceControl',
    Direction: dc.Direction ?? 'PASS',
    CreateLeadImmediately: !!dc.CreateLeadImmediately,
    AutoCheckInMilliSeconds: dc.AutoCheckInMilliSeconds ?? 2000,
    PrintScreenMilliSeconds: dc.PrintScreenMilliSeconds ?? 2500,

    // device capabilities
    IsLineaProEnabled: !!dc.IsLineaProEnabled,
    IsCameraEnabled: !!dc.IsCameraEnabled,
    IsPresentCountEnabled: !!dc.IsPresentCountEnabled,
    IsShowParticipantNameEnabled: !!dc.IsShowParticipantNameEnabled,
    IsShowVersionEnabled: dc.IsShowVersionEnabled ?? true,
    IsLottiesAnimationsEnabled:
        dc.IsLottiesAnimationsEnabled !== undefined ? !!dc.IsLottiesAnimationsEnabled : true,

    // metadata/advanced
    CustomFields: Array.isArray(dc.CustomFields) ? dc.CustomFields : [],
    ServerOverrideUrl: dc.ServerOverrideUrl ?? null,

    // terms
    termsAccepted: !!dc.termsAccepted,
    termsAcceptedBy: dc.termsAcceptedBy ?? null,
    termsAcceptedAt: dc.termsAcceptedAt ? new Date(dc.termsAcceptedAt).toISOString() : null,

    // timestamps
    createdAt: dc.createdAt ? new Date(dc.createdAt).toISOString() : null,
    updatedAt: dc.updatedAt ? new Date(dc.updatedAt).toISOString() : null,
});

// Alias used below to make intent clear
const normalizeDeviceConfig = toExportDeviceConfig;

// Device export with full metadata + _id
export const toExportShape = (d) => ({
    // identity
    _id: d._id,
    SerialNumber: d.SerialNumber,
    Name: d.Name,

    // normalized platform
    Platform: d.Platform === 'MacCatalyst' ? 'iOS' : d.Platform,

    // extra metadata from your schema
    VersionString: d.VersionString ?? null,
    Manufacturer: d.Manufacturer ?? null,
    Idiom: d.Idiom ?? null,
    Model: d.Model ?? null,
    Type: d.Type ?? null,

    // states
    isActive: !!d.isActive,
    isDeleted: !!d.isDeleted,

    // last fetch timestamp
    LastConfigFetch: d.LastConfigFetch ? new Date(d.LastConfigFetch).toISOString() : null,

    // client (object)
    client: d.clientID
        ? {
            _id: d.clientID._id ?? d.clientID,
            name: d.clientID.name ?? null,
            username: d.clientID.username ?? null,
        }
        : null,

    // device config (object)
    DeviceConfigRef: d.DeviceConfigId
        ? {
            _id: d.DeviceConfigId._id ?? d.DeviceConfigId,
            DeviceConfigName: d.DeviceConfigId.DeviceConfigName || d.DeviceConfigName || null,
            SelectedEventId: d.DeviceConfigId.SelectedEventId?._id || null,
            SelectedEventMnemonic: d.DeviceConfigId.SelectedEventId?.mnemonic || null,
        }
        : null,

    // optional hints
    hints: {
        lastObservedHost: d.lastObservedHost ?? null,
    },
});

/* ------------------------------ Small utils ------------------------------ */

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const fetchEach = async (ids, makeUrl) => {
    const unique = uniq(ids);
    if (!unique.length) return [];
    const results = await Promise.allSettled(unique.map((id) => axios.get(makeUrl(id))));
    return results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value?.data)
        .filter(Boolean);
};

// Collect ids from devices (clients, configs, events found inside configs if populated)
const collectRefIdsFromDevices = (devices) => {
    const clientIds = [];
    const configIds = [];
    const eventIds = [];
    for (const d of devices) {
        const cId = d?.clientID?._id || d?.clientID || null;
        const cfg = d?.DeviceConfigId;
        const cfgId = cfg?._id || cfg || null;
        if (cId) clientIds.push(cId);
        if (cfgId) configIds.push(cfgId);
        const evId = cfg?.SelectedEventId?._id || null;
        if (evId) eventIds.push(evId);
    }
    return { clientIds, configIds, eventIdsFromDevices: eventIds };
};

const collectEventIdsFromConfigs = (configs) => {
    const ids = [];
    for (const cfg of configs || []) {
        const evId = cfg?.SelectedEventId?._id || cfg?.SelectedEventId || null;
        if (evId) ids.push(evId);
    }
    return ids;
};

/* -------------------------- Reference Orchestration -------------------------- */
/**
 * Strategy:
 * 1) From devices, fetch missing clients and deviceConfigs by id.
 * 2) From deviceConfigs, gather SelectedEventId list and fetch events by id.
 * 3) Build maps (clientById, eventById).
 * 4) Normalize deviceConfigs using those maps so clientID/SelectedEventId are objects.
 * 5) Normalize events; if an event lacks client info, infer it from the config that references it.
 */
const fetchRefsForDevices = async (devices) => {
    const { clientIds, configIds, eventIdsFromDevices } = collectRefIdsFromDevices(devices);

    // 1) get raw configs by id (not guaranteed populated)
    const configsRaw = await fetchEach(configIds, (id) => `/deviceConfigs/admin/${id}`);

    // add client ids from configs
    const clientIdsFromConfigs = configsRaw
        .map((c) => (c?.clientID?._id || c?.clientID || null))
        .filter(Boolean);

    // 2) now fetch all needed clients
    const allClientIds = uniq([...clientIds, ...clientIdsFromConfigs]);
    const clientsRaw = await fetchEach(allClientIds, (id) => `/clients/${id}`);

    // 3) gather event ids (from devices + from configs) and fetch events
    const eventIdsFromConfigs = collectEventIdsFromConfigs(configsRaw);
    const allEventIds = uniq([...eventIdsFromDevices, ...eventIdsFromConfigs]);
    const eventsRaw = await fetchEach(allEventIds, (id) => `/events/admin/${id}`);

    // 4) build maps
    const clientById = new Map(clientsRaw.map((c) => [String(c._id), normalizeClient(c)]));
    const eventByIdRaw = new Map(eventsRaw.map((e) => [String(e._id), e]));

    // helper: get normalized client object by id
    const pickClientObj = (id) => {
        const obj = clientById.get(String(id));
        return obj ? { _id: obj._id, name: obj.name, username: obj.username } : (id ? { _id: id } : null);
        // keep the ref short in configs/events; full client objects are in refs.clients
    };

    // 5) normalize configs with populated client + event objects (from maps)
    const configsNorm = configsRaw.map((cfg) => {
        const out = { ...cfg };
        const cId = cfg?.clientID?._id || cfg?.clientID || null;
        const eId = cfg?.SelectedEventId?._id || cfg?.SelectedEventId || null;

        out.clientID = cId ? pickClientObj(cId) : null;
        if (eId) {
            const ev = eventByIdRaw.get(String(eId));
            out.SelectedEventId = ev
                ? { _id: ev._id, mnemonic: ev.mnemonic ?? null, beginDate: ev.beginDate ?? null }
                : { _id: eId }; // minimal fallback
        } else {
            out.SelectedEventId = null;
        }
        return normalizeDeviceConfig(out);
    });

    // Map eventId -> client (inferred from any config that references it)
    const eventClientFallback = new Map();
    for (const cfg of configsNorm) {
        const evId = cfg.SelectedEventId?._id;
        const cli = cfg.clientID;
        if (evId && cli && !eventClientFallback.has(String(evId))) {
            eventClientFallback.set(String(evId), cli);
        }
    }

    // 6) normalize events; inject client from config if missing/unpopulated
    const eventsNorm = Array.from(eventByIdRaw.values()).map((ev) => {
        const norm = normalizeEvent(ev);
        const evId = String(norm._id);
        const hasClientObj =
            norm.clientID && typeof norm.clientID === 'object' && (norm.clientID.name || norm.clientID.username);

        if (!hasClientObj && eventClientFallback.has(evId)) {
            // enrich with client from the config that uses this event
            norm.clientID = eventClientFallback.get(evId);
        }
        return norm;
    });

    // 7) normalized clients
    const clientsNorm = Array.from(clientById.values());

    return {
        clients: clientsNorm,
        deviceConfigs: configsNorm,
        events: eventsNorm,
    };
};

/* --------------------------------- Bundle --------------------------------- */

export const buildExportBundle = ({ devices, refs }) => ({
    dsync: {
        bundleVersion: 3,
        exportedAt: new Date().toISOString(),
        count: devices.length,
        refs: {
            clients: refs?.clients?.length || 0,
            deviceConfigs: refs?.deviceConfigs?.length || 0,
            events: refs?.events?.length || 0,
        },
    },
    clients: refs?.clients || [],
    events: refs?.events || [],
    deviceConfigs: refs?.deviceConfigs || [],
    devices: devices.map(toExportShape),
});

/* -------------------------------- Download -------------------------------- */

export const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

/* ------------------------------ Public API ------------------------------ */

export const exportSelectedDevices = async ({
                                                devices,
                                                selectedRowKeys,
                                                messageApi,
                                                setExportLoading,
                                            }) => {
    if (!selectedRowKeys?.length) {
        messageApi?.warning('Select at least 1 device.');
        return;
    }
    setExportLoading?.(true);
    try {
        const map = new Map(devices.map((d) => [d._id, d]));
        const toExport = selectedRowKeys.map((id) => map.get(id)).filter(Boolean);

        const refs = await fetchRefsForDevices(toExport);
        const bundle = buildExportBundle({ devices: toExport, refs });

        downloadJSON(bundle, `dsync-devices-selected-${toExport.length}.json`);
        messageApi?.success(`Exported ${toExport.length} devices.`);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        messageApi?.error('Error while exporting.');
    } finally {
        setExportLoading?.(false);
    }
};

export const exportAllDevices = async ({
                                           filteredDevices,
                                           messageApi,
                                           setExportLoading,
                                       }) => {
    setExportLoading?.(true);
    try {
        const refs = await fetchRefsForDevices(filteredDevices);
        const bundle = buildExportBundle({ devices: filteredDevices, refs });

        downloadJSON(bundle, `dsync-devices-filtered-${filteredDevices.length}.json`);
        messageApi?.success(`Exported ${filteredDevices.length} devices.`);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        messageApi?.error('Error while exporting.');
    } finally {
        setExportLoading?.(false);
    }
};